import { useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/** Fetch listings with optional filters, debounced.
 * `seed`: optionele rotatie-seed. Dezelfde seed = dezelfde (geschudde) volgorde,
 * dus meesturen bij ELKE aanroep binnen één paginalading houdt de volgorde stabiel.
 */
export const useListings = ({ q = "", category = "", soort = "", seed = "" } = {}) => {
  const [data, setData] = useState({ items: [], count: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const params = { limit: 500 };
        if (q) params.q = q;
        if (category) params.category = category;
        if (soort) params.soort = soort;
        if (seed) params.seed = seed;
        const res = await axios.get(`${API}/listings`, {
          params,
          signal: controller.signal,
        });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!axios.isCancel(e) && !cancelled) {
          setError(e);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200); // debounce
    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [q, category, soort, seed]);

  return { ...data, loading, error };
};

export const useFacets = () => {
  const [facets, setFacets] = useState({
    categories: [],
    soorten: [],
    total: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/listings/facets`);
        if (!cancelled) setFacets(res.data);
      } catch (e) {
        // silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { ...facets, loading };
};

export const fetchListing = async (slug) => {
  const res = await axios.get(`${API}/listings/${slug}`);
  return res.data;
};

export const fetchRelated = async (slug, limit = 3, category = null) => {
  const params = { limit };
  if (category) params.category = category;
  const res = await axios.get(`${API}/listings/${slug}/related`, { params });
  return res.data;
};

export const CATEGORY_SLUGS = [
  "eten-drinken",
  "winkelen",
  "mode",
  "wonen-interieur",
  "gezondheid-beauty",
  "diensten",
  "overnachten",
  "cultuur-vrije-tijd",
];

// Labels per taal — één plek om te wijzigen.
export const CATEGORY_LABELS_I18N = {
  "eten-drinken":       { nl: "Eten & Drinken",      de: "Essen & Trinken",    en: "Food & Drink" },
  winkelen:             { nl: "Winkelen",            de: "Einkaufen",          en: "Shopping" },
  mode:                 { nl: "Mode",                de: "Mode",               en: "Fashion" },
  "wonen-interieur":    { nl: "Wonen & Interieur",   de: "Wohnen & Interieur", en: "Home & Interior" },
  "gezondheid-beauty":  { nl: "Gezondheid & Beauty", de: "Gesundheit & Beauty",en: "Health & Beauty" },
  diensten:             { nl: "Diensten",            de: "Dienstleistungen",   en: "Services" },
  overnachten:          { nl: "Overnachten",         de: "Übernachten",        en: "Stay" },
  "cultuur-vrije-tijd": { nl: "Cultuur & Vrije tijd",de: "Kultur & Freizeit",  en: "Culture & Leisure" },
  // Legacy/synoniemen — voor backward compatibility
  beleven:              { nl: "Zien & Doen",         de: "Sehen & Erleben",    en: "See & Do" },
  verblijf:             { nl: "Overnachten",         de: "Übernachten",        en: "Stay" },
};

export const categoryLabel = (slug, lang = "nl") =>
  CATEGORY_LABELS_I18N[slug]?.[lang] || CATEGORY_LABELS_I18N[slug]?.nl || slug;

// Legacy export — behoud voor plekken die enkel NL nodig hebben.
export const CATEGORY_LABELS = Object.fromEntries(
  Object.entries(CATEGORY_LABELS_I18N).map(([k, v]) => [k, v.nl]),
);

// Path-slug voor de publieke categoriepagina's (URL segments).
// Gebruikt om routes en menu op te bouwen. Note: URL keeps hyphens.
export const CATEGORY_PATHS = {
  "eten-drinken":       "eten-en-drinken",
  winkelen:             "winkelen",
  mode:                 "mode",
  "wonen-interieur":    "wonen-interieur",
  "gezondheid-beauty":  "gezondheid-beauty",
  diensten:             "diensten",
  overnachten:          "overnachten",
  "cultuur-vrije-tijd": "cultuur-vrije-tijd",
};


// Categorie-opties voor <select>-inputs. Auto-gegenereerd uit CATEGORY_SLUGS
// (single source of truth) — dit voorkomt drift tussen submissieformulier,
// bedrijvenbeheer en publieke gids.
export const CATEGORY_OPTIONS = CATEGORY_SLUGS.map((slug) => ({
  value: slug,
  label: CATEGORY_LABELS_I18N[slug]?.nl || slug,
}));
